import React, { useState, useEffect, useMemo } from 'react';
import { DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import useEntityTagging from '@/components/hooks/useEntityTagging';
import EntityTagInput from '@/components/common/EntityTagInput';
import StatusFlagButtons from '@/components/common/StatusFlagButtons';

export default function ProgressReportModal({ activity, open, onClose, onSubmit, officer, editingReport, progressReports = [] }) {
    const [krUpdates, setKrUpdates] = useState([]);
    const [proposedStatusFlag, setProposedStatusFlag] = useState("yellow");
    const [issuesDescription, setIssuesDescription] = useState("");
    const [currentUserEmail, setCurrentUserEmail] = useState(null);

    useEffect(() => {
        base44.auth.me().then(user => setCurrentUserEmail(user?.email)).catch(() => {});
    }, []);

    // Get the last approved report
    const lastApprovedReport = useMemo(() => {
        const approved = progressReports.filter(r => r.approved);
        if (approved.length === 0) return null;
        return approved.sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at))[0];
    }, [progressReports]);

    // Initialize form when editing or creating
    useEffect(() => {
        if (editingReport) {
            // Editing mode - populate from existing report
            setKrUpdates(
                (activity.key_results || []).map(kr => {
                    const existingProgress = editingReport.key_results_progress?.find(p => p.description === kr.description);
                    return {
                        description: kr.description,
                        current_value: existingProgress?.new_value || kr.current_value || "0",
                        target: kr.target || "1",
                        unit: kr.unit || ""
                    };
                })
            );
            setProposedStatusFlag(editingReport.proposed_status_flag || "yellow");
            setIssuesDescription(editingReport.issues_description || "");
        } else {
            // Creating mode - use last APPROVED report values, or activity defaults
            if (lastApprovedReport) {
                setKrUpdates(
                    (activity.key_results || []).map(kr => {
                        const approvedProgress = lastApprovedReport.key_results_progress?.find(p => p.description === kr.description);
                        return {
                            description: kr.description,
                            current_value: approvedProgress?.new_value || kr.current_value || "0",
                            target: kr.target || "1",
                            unit: kr.unit || ""
                        };
                    })
                );
                setProposedStatusFlag(lastApprovedReport.proposed_status_flag || activity.status_flag || "yellow");
            } else {
                setKrUpdates(
                    (activity.key_results || []).map(kr => ({
                        description: kr.description,
                        current_value: kr.current_value || "0",
                        target: kr.target || "1",
                        unit: kr.unit || ""
                    }))
                );
                setProposedStatusFlag(activity.status_flag || "yellow");
            }
            setIssuesDescription("");
        }
    }, [editingReport, activity, open, lastApprovedReport]);

    // Fetch resources for tagging
    const { data: officersList = [] } = useQuery({ queryKey: ['officers'], queryFn: () => base44.entities.OfficerProfile.list() });
    const { data: artifacts = [] } = useQuery({ queryKey: ['artifacts'], queryFn: () => base44.entities.Artifact.list() });
    const { data: policies = [] } = useQuery({ queryKey: ['policies'], queryFn: () => base44.entities.Policy.list() });
    const { data: rituals = [] } = useQuery({ queryKey: ['rituals'], queryFn: () => base44.entities.Ritual.list() });
    const { data: capabilities = [] } = useQuery({ queryKey: ['capabilities'], queryFn: () => base44.entities.Capability.list() });

    // Use the unified tagging hook - inherit from last approved report when creating new
    const initialTags = editingReport?.tags || (lastApprovedReport?.tags && !editingReport ? lastApprovedReport.tags : null);
    
    const tagging = useEntityTagging({
        officers: officersList,
        artifacts,
        rituals,
        policies,
        capabilities,
        currentUserEmail,
        initialTags
    });

    const handleKrChange = (index, newValue) => {
        const newUpdates = [...krUpdates];
        newUpdates[index].current_value = newValue;
        setKrUpdates(newUpdates);
    };

    const handleSubmit = async () => {
        const reportData = {
            activity_id: activity.id,
            key_results_progress: krUpdates.map(kr => ({ description: kr.description, new_value: kr.current_value })),
            proposed_status_flag: proposedStatusFlag,
            issues_description: issuesDescription,
            tags: tagging.tags,
            reported_at: editingReport ? editingReport.reported_at : new Date().toISOString()
        };
        
        if (editingReport) {
            // Update existing report
            await base44.entities.ProgressReport.update(editingReport.id, reportData);
            onClose();
        } else {
            // Create new report - issue will be created when report is approved
            onSubmit(reportData);
        }
    };

    return (
        <ConfirmableDialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="civ-progressreportmodal-dialog" className="bg-[#0B1221] border-slate-800 text-slate-100 max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-emerald-400 flex items-center gap-2">
                        {editingReport ? 'Edit Progress Report' : 'Submit Progress Report'}
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                        Update key results and flag any issues.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    {/* KR Updates */}
                    <div className="space-y-3">
                        <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Key Results</Label>
                        {krUpdates.map((kr, idx) => (
                            <div key={idx} className="bg-slate-900/50 p-3 rounded border border-slate-800 flex items-center justify-between gap-4">
                                <div className="text-sm text-slate-300 flex-1">{kr.description}</div>
                                <div className="flex items-center gap-2 w-32 shrink-0">
                                    <Input data-testid="civ-progressreportmodal-input" 
                                        value={kr.current_value} 
                                        onChange={(e) => handleKrChange(idx, e.target.value)}
                                        className="h-8 bg-slate-950 border-slate-700 text-right font-mono"
                                    />
                                    <span className="text-xs text-slate-500 w-8">/ {kr.target} {kr.unit}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Proposed Status Flag */}
                    <div className="space-y-3">
                        <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Proposed Status Flag</Label>
                        <p className="text-xs text-slate-400">This will be applied when the accountable officer approves the report.</p>
                        <StatusFlagButtons
                            value={proposedStatusFlag}
                            onChange={setProposedStatusFlag}
                            variant="large"
                        />
                    </div>

                    {/* Issues & Tags */}
                    <div className="space-y-3">
                        <Label className="text-xs font-bold uppercase text-slate-500 tracking-widest">Issues & Context</Label>
                        <Textarea data-testid="civ-progressreportmodal-textarea" 
                            value={issuesDescription}
                            onChange={(e) => setIssuesDescription(e.target.value)}
                            placeholder="Describe any issues, blockers, or context..."
                            className="bg-slate-900 border-slate-700 min-h-[100px]"
                        />
                        
                        <div className="space-y-2">
                            <EntityTagInput
                                tags={tagging.tags}
                                onAddTag={tagging.addTag}
                                onRemoveTag={tagging.removeTag}
                                getTagOptions={tagging.getTagOptions}
                                currentTagType={tagging.currentTagType}
                                onTagTypeChange={tagging.setCurrentTagType}
                                officers={officersList}
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} className="text-slate-400" data-testid="civ-cancel-btn">Cancel</Button>
                    <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                        {editingReport ? 'Update Report' : 'Submit Report'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </ConfirmableDialog>
    );
}